/**
 * Plugin do Vuex que liga o módulo do Assistente ao log da conversa.
 *
 * Duas coisas, e só para o namespace `assistentePontotel/`:
 *
 * 1. a conversa do log segue a SESSÃO: `definirSessao` é a única escrita de
 *    `sessionId`, e `redefinir` a apaga. Seguir a mutação (em vez de chamar o
 *    log de dentro de cada action) cobre também o reenvio por conflito de
 *    revisão, que troca de sessão por um caminho lateral;
 * 2. toda mutation e toda action do módulo viram entrada de log — com payload,
 *    duração e erro. É o rastro de "o que o front fez" que nenhum backend vê.
 *
 * Erros não tratados da janela entram quando a pilha passa pelo módulo.
 */
import type { Store } from 'vuex'
import { criarLog, definirConversaDoAssistente } from '../servicos/logDoAssistente'

const PREFIXO = 'assistentePontotel/'
const MUTACOES_DA_CONVERSA = new Set([`${PREFIXO}definirSessao`, `${PREFIXO}redefinir`])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EstadoRaiz = { assistentePontotel?: { sessionId?: string | null } } & Record<string, any>

export function pluginDeLogDoAssistente (store: Store<EstadoRaiz>): void {
  const logDoStore = criarLog('store')
  const inicios = new WeakMap<object, number>()

  store.subscribe((mutacao, estado) => {
    if (!mutacao.type.startsWith(PREFIXO)) return
    logDoStore.debug(`mutation ${mutacao.type.slice(PREFIXO.length)}`, mutacao.payload)
    if (MUTACOES_DA_CONVERSA.has(mutacao.type)) {
      definirConversaDoAssistente(estado.assistentePontotel?.sessionId ?? null)
    }
  })

  store.subscribeAction({
    before: (acao) => {
      if (!acao.type.startsWith(PREFIXO)) return
      inicios.set(acao, performance.now())
      logDoStore.debug(`action ${acao.type.slice(PREFIXO.length)} ▶`, acao.payload)
    },
    after: (acao) => {
      if (!acao.type.startsWith(PREFIXO)) return
      const inicio = inicios.get(acao)
      logDoStore.debug(
        `action ${acao.type.slice(PREFIXO.length)} ✔ ${inicio === undefined ? '' : `em ${Math.round(performance.now() - inicio)}ms`}`
      )
    },
    error: (acao, _estado, erro) => {
      if (!acao.type.startsWith(PREFIXO)) return
      logDoStore.error(`action ${acao.type.slice(PREFIXO.length)} ✖ falhou`, erro)
    }
  })

  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  const logDaJanela = criarLog('janela')
  const doAssistente = (pilha: unknown) => typeof pilha === 'string' && /AssistentePontotel|assistente/i.test(pilha)
  window.addEventListener('error', (evento) => {
    if (doAssistente(evento.error?.stack) || doAssistente(evento.filename)) {
      logDaJanela.error(`erro não tratado: ${evento.message}`, evento.error)
    }
  })
  window.addEventListener('unhandledrejection', (evento) => {
    const motivo = evento.reason
    if (doAssistente(motivo?.stack) || doAssistente(String(motivo))) {
      logDaJanela.error('promessa rejeitada sem tratamento', motivo)
    }
  })
}
